import { useCallback, useEffect, useState } from 'react'

/**
 * Routeur minuscule, par chemin.
 *
 * Par chemin et non par ancre, volontairement : Supabase renvoie les
 * jetons du lien magique dans l'ancre de l'URL (#access_token=...) et
 * un routeur par ancre entrerait en conflit avec lui. netlify.toml
 * renvoie toute adresse vers index.html pour que le rechargement d'une
 * page interne fonctionne.
 *
 * Une bibliotheque de routage couterait une dizaine de kilo-octets
 * pour ce que font ces quarante lignes.
 */

export interface Location {
  path: string
  /** Segments du chemin, sans les barres obliques. */
  segments: string[]
}

function read(): Location {
  const path = window.location.pathname || '/'
  return {
    path,
    segments: path.split('/').filter(Boolean),
  }
}

export function useRouter() {
  const [loc, setLoc] = useState<Location>(read)

  useEffect(() => {
    const onPop = () => setLoc(read())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = useCallback(
    (to: string, options?: { replace?: boolean }) => {
      if (to === window.location.pathname) return
      if (options?.replace) window.history.replaceState(null, '', to)
      else window.history.pushState(null, '', to)
      setLoc(read())
      window.scrollTo(0, 0)
    },
    [],
  )

  const goBack = useCallback(() => {
    if (window.history.length > 1) window.history.back()
    else navigate('/')
  }, [navigate])

  return { ...loc, navigate, goBack }
}

/** Onglet du bas correspondant au chemin courant. */
export function tabFor(segments: string[]): string {
  const first = segments[0]
  if (!first) return '/'
  if (['loyers'].includes(first)) return '/loyers'
  if (['depenses'].includes(first)) return '/depenses'
  if (['immeuble', 'locataires', 'baux'].includes(first)) return '/immeuble'
  if (['plus', 'cautions', 'documents', 'rapports', 'banque', 'reglement', 'charges'].includes(first)) return '/plus'
  return '/'
}
